import { styled } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import { useCheckPermission } from '../../shared/Helpers/hooks';
import { PERMISSIONS } from '../../App/constants';
import { SoapNotesStatuses } from '../../redux/API/ClientAPIHelpers/dataCollectionProperties';
import { MBHBillingStatus } from '../../redux/API/ClientAPIHelpers/billingProperties';
import { useSelector } from '../../redux/store';
import { AdminTypes } from '../../components/Menu';

export const HeaderWrapper = styled('div')(() => ({
	display: 'flex',
	alignItems: 'center',
	'&>div': {
		width: '50%',
	},
}));

export const EDITABLE_FIELDS_POST_SUBMIT = [
	'locationId',
	'participantResponses',
	'participantOther',
	'isSessionSupervised',
	'supervisedSessionId',
	'date',
	'startTime',
	'endTime',
	'clientTelehealthLocationId',
	'userTelehealthLocationId',
] as const;

export type EditableFieldPostSubmit = (typeof EDITABLE_FIELDS_POST_SUBMIT)[number];

export const usePostSubmitEditMode = () => {
	const [params] = useSearchParams();
	const role = useSelector((state) => state.account.role);
	const soapNoteStatus = useSelector((state) => state.soapNote.soapNote?.status?.status);
	const POST_SUBMIT_EDIT = useCheckPermission(PERMISSIONS.SOAPNOTE.POST_SUBMIT_EDIT);

	const isPostSubmitParam = params.get('postSubmit') === 'true';
	const isAdmin = role?.section.id === AdminTypes.admin;
	const isSubmitted = soapNoteStatus === SoapNotesStatuses.submitted;

	const isPostSubmitEditMode = isPostSubmitParam && isAdmin && isSubmitted && POST_SUBMIT_EDIT.permissionGranted;

	const isFieldEditable = (fieldName: string): boolean => {
		if (!isPostSubmitEditMode) return true;

		return EDITABLE_FIELDS_POST_SUBMIT.includes(fieldName as EditableFieldPostSubmit);
	};

	return {
		isPostSubmitEditMode,
		isFieldEditable,
		editableFields: EDITABLE_FIELDS_POST_SUBMIT,
	};
};

export const useCanEditSOAPNote = () => {
	const role = useSelector((state) => state.account.role);
	const currentUserId = useSelector((state) => state.account.user.id);
	const soapNoteStatus = useSelector((state) => state.soapNote.soapNote?.status?.status);
	const noteUserId = useSelector((state) => state.soapNote.soapNote?.user?.id);

	const isAdmin = role?.section.id === AdminTypes.admin;
	const canUserEdit = noteUserId === currentUserId || isAdmin;

	if (
		soapNoteStatus === SoapNotesStatuses.submitted ||
		soapNoteStatus === SoapNotesStatuses.expired48 ||
		soapNoteStatus === SoapNotesStatuses.expiredWeek ||
		!canUserEdit
	) {
		return false;
	}

	return true;
};

export const useSessionBillingStatus = () => {
	const billingStatus = useSelector((state) => state.soapNote.soapNote?.mbhBillingStatus ?? null);

	const requiresConfirmation = billingStatus === MBHBillingStatus.Approved || billingStatus === MBHBillingStatus.Billed;

	return {
		billingStatus,
		requiresConfirmation,
	};
};

/**
 * Universal hook for SOAP note field disabled state.
 * Combines canEdit and post-submit edit mode logic into a single hook.
 * Use getFieldDisabled(fieldName) for ALL fields to ensure correct behavior.
 */
export const useSoapNoteFieldDisabled = () => {
	const canEdit = useCanEditSOAPNote();
	const { isPostSubmitEditMode, isFieldEditable } = usePostSubmitEditMode();

	const getFieldDisabled = (fieldName: string): boolean => {
		if (isPostSubmitEditMode) return !isFieldEditable(fieldName);

		return !canEdit;
	};

	return { getFieldDisabled, isPostSubmitEditMode, canEdit };
};

/**
 * Hook to detect if the session duration has been increased compared to the original.
 * Compares current start/end times against the original values loaded from the API.
 */
export const useDurationIncreased = () => {
	const { startTime, endTime } = useSelector((state) => state.session.sessionInfo);
	const originalStartTime = useSelector((state) => state.session.originalStartTime);
	const originalEndTime = useSelector((state) => state.session.originalEndTime);

	if (!startTime || !endTime || !originalStartTime || !originalEndTime) {
		return false;
	}

	const originalDurationMinutes = dayjs(originalEndTime).diff(dayjs(originalStartTime), 'minute');
	const currentDurationMinutes = dayjs(endTime).diff(dayjs(startTime), 'minute');

	return currentDurationMinutes > originalDurationMinutes;
};
